import { useState } from "react";
import {
    Avatar,
    AvatarGradientFallback,
    Badge,
    cn,
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
    getGradientForId,
    Input,
    Text,
} from "@athena/ui";
import { useTranslation } from "react-i18next";
import { Check, Plus } from "lucide-react";
import { getInitialsFrom } from "../../../global/formatHelpers";
import { NewPresentersDialog } from "@/pages/Presenters/NewPresentersDialog";

interface PresenterOption {
    id: string;
    name: string;
    /** Optional: a presenter imported from elsewhere may have no credential line. */
    title?: string | null;
}

interface PresenterModalProps {
    allPresenters: PresenterOption[];
    selectedPresenterIds: Set<string>;
    onToggle: (presenterId: string) => void;
}

export function PresenterModal({ allPresenters, selectedPresenterIds, onToggle }: PresenterModalProps) {
    const { t } = useTranslation();
    const [
        presenterOpen,
        setPresenterOpen,
    ] = useState<boolean>(false);
    const [
        presenterSearch,
        setPresenterSearch,
    ] = useState<string>("");
    const [
        newPresenterOpen,
        setNewPresenterOpen,
    ] = useState<boolean>(false);

    const filteredPresenters = allPresenters.filter((p) =>
        p.name.toLowerCase().includes(presenterSearch.toLowerCase()),
    );

    return (
        <div className="flex flex-col gap-2">
            <NewPresentersDialog open={newPresenterOpen} onOpenChange={setNewPresenterOpen} />
            {selectedPresenterIds.size > 0 && (
                <div className="flex flex-col gap-1.5">
                    {allPresenters
                        .filter((p) => selectedPresenterIds.has(p.id))
                        .map((presenter) => (
                            <div
                                key={presenter.id}
                                className="border-border flex w-fit items-center gap-2 rounded-full border py-1 pr-2 pl-1"
                            >
                                <Avatar className="h-6 w-6 shrink-0 text-[10px]">
                                    <AvatarGradientFallback
                                        from={getGradientForId(presenter.id)[0]}
                                        to={getGradientForId(presenter.id)[1]}
                                    >
                                        {getInitialsFrom(presenter.name)}
                                    </AvatarGradientFallback>
                                </Avatar>
                                <div className="flex flex-col">
                                    <Text size="sm" className="leading-none font-medium whitespace-nowrap">
                                        {presenter.name}
                                    </Text>
                                    <Text size="xs" muted className="mt-1 leading-none whitespace-nowrap">
                                        {presenter.title}
                                    </Text>
                                </div>
                                <button
                                    onClick={() => onToggle(presenter.id)}
                                    className="text-muted-foreground hover:text-foreground ml-0.5 shrink-0 transition-colors"
                                >
                                    <Plus className="h-3.5 w-3.5 rotate-45" />
                                </button>
                            </div>
                        ))}
                </div>
            )}
            <DropdownMenu open={presenterOpen} onOpenChange={setPresenterOpen}>
                <DropdownMenuTrigger asChild>
                    <div className="w-fit">
                        <Badge
                            variant={"dottedOutline"}
                            className={cn(presenterOpen && "border-secondary border-dashed")}
                        >
                            <Plus className="mr-1 h-3.5 w-3.5" />
                            {t("admin.lms.addPresenter")}
                        </Badge>
                    </div>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                    className="bg-card w-96 p-0"
                    align="start"
                    side="bottom"
                    sideOffset={8}
                    avoidCollisions={false}
                >
                    <div className="border-b p-2">
                        <Input
                            placeholder={t("admin.lms.searchPeople")}
                            value={presenterSearch}
                            onChange={(e) => setPresenterSearch(e.target.value)}
                            onKeyDown={(e) => e.stopPropagation()}
                            className="h-8"
                            type="text"
                        />
                    </div>
                    <DropdownMenuGroup className="p-1">
                        {filteredPresenters.map((presenter) => (
                            <DropdownMenuItem
                                key={presenter.id}
                                onSelect={(e) => {
                                    e.preventDefault();
                                    onToggle(presenter.id);
                                }}
                                className={cn(
                                    "my-1 flex items-center gap-3 rounded-lg px-3 py-2",
                                    selectedPresenterIds.has(presenter.id)
                                        ? "border-secondary bg-secondary/10 focus:bg-secondary/20 data-highlighted:bg-secondary/20 border-[0.2px]"
                                        : "bg-transparent",
                                )}
                            >
                                <Avatar>
                                    <AvatarGradientFallback
                                        from={getGradientForId(presenter.id)[0]}
                                        to={getGradientForId(presenter.id)[1]}
                                    >
                                        {getInitialsFrom(presenter.name)}
                                    </AvatarGradientFallback>
                                </Avatar>
                                <div className="flex min-w-0 flex-1 flex-col">
                                    <Text className="leading-tight font-semibold">{presenter.name}</Text>
                                    <Text size="sm" muted className="leading-tight">
                                        {presenter.title}
                                    </Text>
                                </div>

                                <div
                                    className={cn(
                                        "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border",
                                        selectedPresenterIds.has(presenter.id)
                                            ? "border-secondary bg-secondary"
                                            : "border-muted-foreground",
                                    )}
                                >
                                    {selectedPresenterIds.has(presenter.id) && <Check className="h-3 w-3 text-white" />}
                                </div>
                            </DropdownMenuItem>
                        ))}
                    </DropdownMenuGroup>
                    <DropdownMenuSeparator />
                    <DropdownMenuGroup className="p-1">
                        <DropdownMenuItem
                            className="flex items-center gap-3 px-3 py-2"
                            onSelect={(e) => {
                                e.preventDefault();
                                setPresenterOpen(false);
                                setNewPresenterOpen(true);
                            }}
                        >
                            <div className="bg-secondary/10 flex h-9 w-9 shrink-0 items-center justify-center rounded-full">
                                <Plus className="text-secondary h-4 w-4" />
                            </div>
                            <div className="flex flex-col">
                                <Text className="leading-tight font-semibold">{t("admin.lms.createNewPresenter")}</Text>
                                <Text size="sm" muted className="leading-tight">
                                    {t("admin.lms.addSomeoneToRoster")}
                                </Text>
                            </div>
                        </DropdownMenuItem>
                    </DropdownMenuGroup>
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    );
}
